import { NavigationContext } from "@-/com.npmjs.expo-router/react-navigation";
import { useCallback, useContext, useSyncExternalStore } from "react";

/**
 * Whether the screen this hook renders inside is the one the user is looking at.
 *
 * React Navigation's `isFocused()` walks every ancestor navigator, and it re-emits a parent's
 * `focus` / `blur` to the focused child route — so a toolbar nested three navigators deep still
 * reports `false` the moment its bottom tab loses focus. That matters because inactive tabs stay
 * MOUNTED: anything a screen declares about the shared header (opaque chrome, toolbar geometry)
 * has to be scoped to the focused screen, or a blurred tab keeps steering chrome it no longer
 * owns.
 *
 * Outside navigation entirely (an isolated render test, Storybook), the caller counts as focused.
 */
export function useIsNavigationFocused(): boolean {
  const navigation = useContext(NavigationContext);
  const subscribe = useCallback(
    (notify: () => void) => {
      const unsubscribeFocus = navigation?.addListener("focus", notify);
      const unsubscribeBlur = navigation?.addListener("blur", notify);
      return () => {
        unsubscribeFocus?.();
        unsubscribeBlur?.();
      };
    },
    [navigation],
  );
  const getIsFocused = useCallback(() => navigation?.isFocused() ?? true, [navigation]);
  return useSyncExternalStore(subscribe, getIsFocused, getIsFocused);
}
